import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Crown, Check, ArrowRight, Star, Users, Video, MessageSquare } from 'lucide-react';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Upgrade() {
  const navigate = useNavigate();
  const [interested, setInterested] = useState(false);
  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  const handleInterest = async () => {
    try {
      await axios.post(`${API}/mastermind/interest`, { user_id: user.user_id });
      setInterested(true);
      toast.success("You're on the Mastermind waitlist!");
    } catch (e) { toast.error('Something went wrong'); }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        {/* Hero */}
        <div className="text-center mb-12">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="w-20 h-20 bg-gradient-to-br from-[#D4AF37]/20 to-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-[#D4AF37]/30">
            <Crown className="w-10 h-10 text-[#D4AF37]" />
          </motion.div>
          <h1 className="text-4xl sm:text-5xl font-cormorant font-bold text-white mb-3" data-testid="upgrade-title">Private Mastermind</h1>
          <p className="text-[#A39BB0] font-inter text-base max-w-lg mx-auto">
            Bi-weekly strategy calls with founders. Intimate group. Direct access. The fastest path to results.
          </p>
        </div>

        {/* Pricing Card */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
          className="relative overflow-hidden bg-[#2D074E] border-2 border-[#D4AF37]/40 rounded-2xl max-w-lg mx-auto mb-12" data-testid="mastermind-card">
          <div className="absolute inset-0 bg-cover bg-center opacity-15"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1608377733063-8c76fdd47e41?w=600&q=40)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2D074E]/60 to-[#1A0533]/90" />
          <div className="relative p-8">
            <div className="flex items-baseline space-x-1 mb-6 justify-center">
              <span className="text-[#D4AF37] font-cormorant font-bold text-5xl">$297</span>
              <span className="text-[#A39BB0] text-sm font-inter">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                { icon: Video, text: 'Bi-weekly live strategy calls' },
                { icon: Users, text: 'Direct founder access' },
                { icon: MessageSquare, text: 'Private Slack channel' },
                { icon: Star, text: 'Priority roadmap reviews' },
                { icon: Crown, text: '1-on-1 strategy sessions' },
                { icon: Check, text: 'Price lock guarantee' },
              ].map((f, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                  className="flex items-center space-x-3 text-white/90 text-sm font-inter">
                  <f.icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} /><span>{f.text}</span>
                </motion.li>
              ))}
            </ul>
            <motion.button onClick={handleInterest} disabled={interested}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              className={`w-full font-inter font-bold text-sm py-3.5 rounded-full transition-all ${
                interested
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-gradient-to-r from-[#D4AF37] to-amber-500 text-black shadow-[0_0_20px_rgba(212,175,55,0.4)]'
              }`} data-testid="mastermind-join-btn">
              {interested ? 'On the Waitlist' : 'Join the Waitlist'}
            </motion.button>
          </div>
        </motion.div>

        {/* Back */}
        <div className="text-center">
          <button onClick={() => navigate('/dashboard')}
            className="text-[#A39BB0] hover:text-white font-inter text-sm" data-testid="back-to-dashboard">
            Back to Dashboard
          </button>
        </div>
      </div>
    </AppShell>
  );
}
